import { AsyncPipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { NgSelectComponent, NgSelectModule } from '@ng-select/ng-select';
import { SdvorIconComponent, SdvorLoaderComponent } from '@sdvor/shared/ui/v2';
import { DeviceService } from '@shared/services';
import { IconTypes } from '@shared/v2';
import { Suggestion } from '@spartacus/core';
import { GeocodeResult } from '@yandex-maps/const';
import { AddressSuggestionsService, GeocodeService } from '@yandex-maps/feature';
import { debounceTime, Observable, take, timer } from 'rxjs';

@Component({
  selector: 'ui-address-suggestions-select',
  imports: [AsyncPipe, ReactiveFormsModule, NgSelectModule, SdvorLoaderComponent, SdvorIconComponent],
  templateUrl: './address-suggestions-select.component.html',
  styleUrls: ['./address-suggestions-select.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [AddressSuggestionsService],
})
export class AddressSuggestionsSelectComponent implements OnInit {
  @Input() placeholder = 'введите ближайший к вам адрес';
  @Input() showBackArrow = false;
  @Input() suppressEmptyDropdown = false;
  @Output() changeAddress = new EventEmitter<GeocodeResult | undefined>();
  @Output() handleOpened = new EventEmitter<boolean>();
  @Output() backArrowClick = new EventEmitter<void>();

  iconTypes = IconTypes;
  formControl: FormControl = new FormControl(null, { updateOn: 'change', validators: null });
  suggestions$: Observable<Suggestion[]> = this.suggestionsService.suggestions$;
  suggestIsLoading$: Observable<boolean> = this.suggestionsService.loading$;
  isDesktop$: Observable<boolean> = this.deviceService.isDesktopLgUp$;

  private lastGeoObject: GeocodeResult | undefined;

  selectTypeahead = new EventEmitter<string>();

  @ViewChild('addressSuggestionsSelect') addressSuggestionsSelect: NgSelectComponent;

  constructor(
    private suggestionsService: AddressSuggestionsService,
    private geocodeService: GeocodeService,
    private destroyRef: DestroyRef,
    private deviceService: DeviceService,
  ) {}

  ngOnInit(): void {
    this.selectTypeahead.pipe(debounceTime(700), takeUntilDestroyed(this.destroyRef)).subscribe((query) => {
      this.suggestionsService.loadSuggestions(query);
    });
  }

  onInputSelect(e: Event): void {
    const input = e.target as HTMLInputElement;
    const value = input.value;

    this.formControl.setValue(value || null);

    if (!value) {
      this.changeAddress.emit(undefined);
    }
  }

  onChangeSelect(suggestion: any): void {
    if (suggestion) {
      this.geocodeService
        .getGeoObject(suggestion)
        .pipe(take(1))
        .subscribe((geoObject: GeocodeResult) => {
          this.lastGeoObject = geoObject;
          this.changeAddress.emit(geoObject);
        });
    } else {
      this.lastGeoObject = undefined;
      this.changeAddress.emit(undefined);
    }
  }

  onFocusSelect(e: FocusEvent): void {
    const input = e.target as HTMLInputElement;
    const currentValue = this.formControl.value;

    if (currentValue) return;

    if (input.value) {
      this.formControl.setValue(input.value);
    }
  }

  onBlurSelect(e: FocusEvent): void {
    const input = e.target as HTMLInputElement;
    const currentValue = this.formControl.value;

    if (currentValue !== input.value) {
      this.formControl.setValue(input.value.trim() || null);

      return;
    }

    if (!input.value) {
      this.formControl.setValue(null);
    }

    this.handleOpened.emit(false);
  }

  clear(): void {
    this.addressSuggestionsSelect.handleClearClick();
    this.formControl.setValue(null);
    this.handleOpened.emit(true);
  }

  onOpened(): void {
    this.handleOpened.emit(true);
  }

  onClosed(): void {
    if (this.formControl.value && this.lastGeoObject) {
      this.changeAddress.emit(this.lastGeoObject);
    }

    this.handleOpened.emit(false);

    // нужно дождаться пока сработает focusSelect
    timer(100)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.addressSuggestionsSelect.blur();
      });
  }
}
